import { useEffect, useId, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Plus, FileText, Sparkles } from 'lucide-react'
import { SortableInstructionItem } from './sortable-instruction-item'
import {
  useCreateInstruction,
  useUpdateInstruction,
  useDeleteInstruction,
  useReorderInstructions,
  ClassificationRequestError,
  type Instruction,
} from '@/api'
import { InstructionScope, type InstructionScopeValue } from '@/lib/enum-display'

type InstructionListProps = {
  instructions: Instruction[]
  scope: InstructionScopeValue
  scopedId?: string // connectedAccountId, mcpServerId, or toolName
  title: string
  description?: string
  emptyTitle?: string
  emptyDescription?: string
  onDirtyChange?: (dirty: boolean) => void
  starter?: { title: string; content: string }
  // 'section' renders without its own card, for use inside another card.
  variant?: 'card' | 'section'
}

export function InstructionList({
  instructions,
  scope,
  scopedId,
  title,
  description,
  emptyTitle = 'No instructions yet',
  emptyDescription = 'Add your first instruction to get started',
  onDirtyChange,
  starter,
  variant = 'card',
}: InstructionListProps) {
  const prefix = useId()
  const [formError, setFormError] = useState('')
  const [deletingInstruction, setDeletingInstruction] = useState<Instruction | null>(null)
  const [compareLatest, setCompareLatest] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingInstruction, setEditingInstruction] = useState<Instruction | null>(null)
  const [localInstructions, setLocalInstructions] = useState<Instruction[]>(instructions)
  
  // Track if we're currently dragging to avoid overwriting optimistic updates
  const [isDragging, setIsDragging] = useState(false)

  // Keep local state in sync with props (compare full data, not just IDs)
  // But only when not dragging to preserve optimistic reorder updates
  if (!isDragging && JSON.stringify(instructions) !== JSON.stringify(localInstructions)) {
    setLocalInstructions(instructions)
  }

  const [formData, setFormData] = useState({
    title: '',
    content: '',
  })
  const dirty = formData.title !== (editingInstruction?.title ?? '') || formData.content !== (editingInstruction?.content ?? '')
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const createMutation = useCreateInstruction()
  const updateMutation = useUpdateInstruction()
  const deleteMutation = useDeleteInstruction()
  const reorderMutation = useReorderInstructions()

  const resetForm = () => {
    setFormError('')
    setCompareLatest(false)
    setFormData({ title: '', content: '' })
    setEditingInstruction(null)
    setIsDialogOpen(false)
  }

  const handleEdit = (instruction: Instruction) => {
    setEditingInstruction(instruction)
    setFormData({
      title: instruction.title,
      content: instruction.content,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = () => {
    setFormError('')
    if (editingInstruction) {
      updateMutation.mutate(
        { id: editingInstruction.uuid, data: { ...formData, version: editingInstruction.version } },
        {
          onSuccess: () => {
            toast.success('Instruction updated')
            resetForm()
          },
          onError: error => setFormError(error instanceof ClassificationRequestError && error.status === 409 ? 'This instruction changed elsewhere. Review the saved instruction. Your edits are retained.' : 'Your instruction could not be saved. Your edits are retained. Try again.'),
        }
      )
    } else {
      const createData: {
        title: string
        content: string
        scope: InstructionScopeValue
        connectedAccountId?: string
        mcpServerId?: string
        toolName?: string
      } = {
        ...formData,
        scope,
      }

      if (scope === InstructionScope.ACCOUNT && scopedId) {
        createData.connectedAccountId = scopedId
      } else if (scope === InstructionScope.SERVER && scopedId) {
        createData.mcpServerId = scopedId
      } else if (scope === InstructionScope.TOOL && scopedId) {
        createData.toolName = scopedId
      }

      createMutation.mutate(createData, {
        onSuccess: () => {
          toast.success('Instruction created')
          resetForm()
        },
        onError: () => setFormError('Your instruction could not be saved. Your edits are retained. Try again.'),
      })
    }
  }

  const handleToggle = (instruction: Instruction, is_active: boolean) => {
    updateMutation.mutate(
      { id: instruction.uuid, data: { isActive: is_active, version: instruction.version } },
      {
        onSuccess: () => toast.success('Instruction updated'),
        onError: () => toast.error('Failed to update instruction'),
      }
    )
  }

  const handleDelete = (instruction: Instruction) => {
    deleteMutation.mutate({ id: instruction.uuid, version: instruction.version }, {
      onSuccess: () => { toast.success('Instruction deleted'); setDeletingInstruction(null) },
      onError: error => toast.error(error instanceof ClassificationRequestError && error.status === 409 ? 'This instruction changed. Close this dialog and review the saved instruction.' : 'Failed to delete instruction'),
    })
  }

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setIsDragging(false)

    if (over && active.id !== over.id) {
      setLocalInstructions((items) => {
        const oldIndex = items.findIndex((i) => i.uuid === active.id)
        const newIndex = items.findIndex((i) => i.uuid === over.id)
        const newItems = arrayMove(items, oldIndex, newIndex)

        // Send reorder request
        const reorderItems = newItems.map((item, index) => ({
          id: item.uuid,
          order: index,
          version: item.version,
        }))
        reorderMutation.mutate(reorderItems, {
          onError: () => {
            toast.error('Failed to reorder instructions')
            setLocalInstructions(instructions) // Reset to original order
          },
        })

        return newItems
      })
    }
  }

  const openAdd = () => {
    if (!editingInstruction && !formData.title && !formData.content && starter) setFormData(starter)
    setIsDialogOpen(true)
  }
  const addButton = (
    <Button
      size="sm"
      variant={variant === 'section' ? 'outline' : 'default'}
      onClick={openAdd}
      aria-label={`Add ${title.toLowerCase()}`}
      className="gap-2"
    >
      <Plus className="h-4 w-4" />
      Add
    </Button>
  )
  const list = (
    <>
        {localInstructions.length > 0 ? (
          <DndContext
            id={`${prefix}-instructions`}
            accessibility={{ announcements: {
              onDragStart: ({ active }) => `Moving ${localInstructions.find(item => item.uuid === active.id)?.title ?? 'instruction'}.`,
              onDragOver: ({ active, over }) => over ? `Moving ${localInstructions.find(item => item.uuid === active.id)?.title ?? 'instruction'} to ${localInstructions.find(item => item.uuid === over.id)?.title ?? 'new position'}.` : undefined,
              onDragEnd: () => 'Instruction order updated.',
              onDragCancel: () => 'Instruction move cancelled.',
            } }}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localInstructions.map((i) => i.uuid)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 stagger-children">
                {localInstructions.map((instruction) => (
                  <SortableInstructionItem
                    key={instruction.uuid}
                    instruction={instruction}
                    onEdit={handleEdit}
                    onDelete={setDeletingInstruction}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted mb-3">
              <Sparkles className="h-6 w-6 text-muted-foreground" />
            </div>
            <h4 className="text-sm font-semibold">{emptyTitle}</h4>
            <p className="mt-1 text-xs text-muted-foreground max-w-sm">{emptyDescription}</p>
          </div>
        )}
    </>
  )
  const dialogs = (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sentry-block ph-no-capture max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInstruction ? 'Edit instruction' : 'Add instruction'}
            </DialogTitle>
            <DialogDescription>
              {scope === InstructionScope.USER ? 'Applies to all your inboxes.' : 'Applies to this inbox, on top of your overall instructions.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor={`${prefix}-title`}>Title</Label>
              <Input
                id={`${prefix}-title`}
                maxLength={200}
                value={formData.title}
                placeholder="Newsletters"
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="transition-all duration-200"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`${prefix}-content`}>Instruction</Label>
              <Textarea
                id={`${prefix}-content`}
                className="min-h-[150px] text-sm transition-all duration-200"
                placeholder="Mark newsletters read and archive them."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>
          </div>
          {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
          {updateMutation.error instanceof ClassificationRequestError && updateMutation.error.status === 409 && editingInstruction && <div className="space-y-3 rounded-lg border p-4">
            <Button variant="outline" onClick={() => setCompareLatest(true)}>Review latest instruction</Button>
            {compareLatest && instructions.filter(instruction => instruction.uuid === editingInstruction.uuid).map(instruction => <div key={instruction.uuid} className="space-y-2">
              <h3 className="text-sm font-semibold">{instruction.title}</h3>
              <p className="whitespace-pre-wrap break-words text-sm">{instruction.content}</p>
              <p className="text-xs text-muted-foreground">{instruction.is_active ? 'Enabled' : 'Disabled'}</p>
              <Button variant="outline" onClick={() => { setEditingInstruction({ ...editingInstruction, version: instruction.version }); setCompareLatest(false); updateMutation.reset(); setFormError('Your edits are retained. Save to replace the saved instruction.') }}>Keep my edits</Button>
            </div>)}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.title.trim() || !formData.content.trim() || createMutation.isPending || updateMutation.isPending}
              className="gap-2"
            >
              {editingInstruction ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deletingInstruction} onOpenChange={open => { if (!open) setDeletingInstruction(null) }}>
        <DialogContent className="sentry-block ph-no-capture">
          <DialogHeader>
            <DialogTitle>Delete instruction?</DialogTitle>
            <DialogDescription>“{deletingInstruction?.title}” will no longer apply to new email.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingInstruction(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => {
              if (deletingInstruction) handleDelete(deletingInstruction)
            }}>Delete instruction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  if (variant === 'section')
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold">{title}</h2>
          {addButton}
        </div>
        {list}
        {dialogs}
      </section>
    )

  return (
    <Card className="overflow-hidden pt-0">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 border-b bg-muted/30 pt-6">
        <div>
          <CardTitle role="heading" aria-level={2} className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {title}
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {addButton}
      </CardHeader>
      <CardContent>{list}</CardContent>
      {dialogs}
    </Card>
  )
}
